/* eslint-disable @typescript-eslint/no-unused-expressions */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import cls from 'classnames';
import { Input, Toast } from '@/components';
import { checkForgetPwd, sendBindCode, sendVerifyCodeV2, sendVerifyCodeV3 } from '@/api/login';
import { REGEXP_EMAIL } from '@/utils/constants';
import Image from 'next/image';
import styles from './style.module.scss';
import VerifySlideFixed, { CaptchaSuccess } from '@/pageComponents/verifition/verifySlideFixed';

export interface CodeInputProps {
  placeholder: string;
  className?: string;
  onChange: (value: string) => void;
  target: string;
  maxLength?: number;
  onPressEnter?: () => void;
  type: 'EMAIL' | 'MOBILE_PHONE';
  way: 'LOGIN' | 'REGISTER';
  mode?: 'Normal' | 'Bind';
  onShowMsg?: () => void;
  codeClassName?: string;
  auto?: boolean;
  value?: string;
  apiUrl?: string;
  special?: boolean; // 特殊处理
  onNeedRegister?: (value: boolean) => void;
  onVerifyClick?: (flag: boolean) => void;
}

const DEFAULT_COUNTDOWN_TIME: number = 60;

const CodeInput: React.FC<CodeInputProps> = (props) => {
  const {
    placeholder,
    className,
    onChange,
    target,
    maxLength,
    type,
    codeClassName,
    mode = 'Normal',
    way,
    value,
    onShowMsg,
    onPressEnter,
    apiUrl,
    special,
    onNeedRegister,
    onVerifyClick,
  } = props;
  const [sendFlag, setSendFlag] = useState(false);
  const { t } = useTranslation('home');
  const DEFAULT_CODE_TEXT = t('获取验证码-test');
  const [countDown, setCountDown] = useState<number>(-1);
  const [sendingCode, setSendingCode] = useState<boolean>(false);
  // 无区号的电话号码, 主要用来做验证按钮校验
  const [phone, setPhone] = useState<string>('');
  const [codeText, setCodeText] = useState(DEFAULT_CODE_TEXT); // 倒计时时长
  const [isSlideShow, setIsSlideShow] = React.useState<boolean>(false);

  const verifyTarget = useCallback((): boolean => {
    let result = false;
    if (type === 'EMAIL') {
      result = !REGEXP_EMAIL.test(target as string);
      result && Toast.warning(t('邮箱格式错误'));
      return result;
    }
    if (type === 'MOBILE_PHONE' && !/^\d+$/.test(target?.split?.(' ')?.[1])) {
      Toast.warning(t('请输入有效手机号'));
      return true;
    }
    return result;
  }, [t, target, type]);

  const cancelSendLoading = () => {
    setTimeout(() => {
      setSendingCode(false);
    }, 1e3);
  };

  // 发送验证码
  const sendCode = useCallback(
    async (code?: string) => {
      if (sendFlag) {
        setSendingCode(false);
        return;
      }
      setSendFlag(true);
      if (codeText !== DEFAULT_CODE_TEXT || verifyTarget()) {
        setSendFlag(false);
        setSendingCode(false);
        return;
      }
      if (special) {
        try {
          const { errorCode = '' } = await checkForgetPwd(type === 'EMAIL' ? 'EMAIL' : 'MOBILE', target);
          if (errorCode === 1220) {
            onShowMsg?.();
            setSendingCode(false);
            setSendFlag(false);
            return;
          }
          if (errorCode === 1221) {
            onNeedRegister?.(true);
            setSendingCode(false);
            setSendFlag(false);
            return;
          }
        } catch {
          setSendingCode(false);
          setSendFlag(false);
        }
      }

      if (apiUrl) {
        try {
          const res = await sendVerifyCodeV3(apiUrl, {
            target,
            verificationType: type,
            authenticationWay: way,
            captchaVerification: code,
          });
          const { success, errorCode } = res;
          if (success === false && errorCode === 1201) {
            onShowMsg?.();
            setSendingCode(false);
            setSendFlag(false);
            return;
          }
          setCountDown(DEFAULT_COUNTDOWN_TIME);
          cancelSendLoading();
        } catch {
          setSendingCode(false);
          setSendFlag(false);
        }
      }

      if (mode === 'Normal') {
        try {
          const res = await sendVerifyCodeV2({
            target,
            verificationType: type,
            authenticationWay: way,
            captchaVerification: code,
          });
          const { errorCode } = res || {};
          if (errorCode === 407 || errorCode === 406) {
            onShowMsg?.();
            setSendingCode(false);
            setSendFlag(false);
            return;
          }
          setCountDown(DEFAULT_COUNTDOWN_TIME);
          cancelSendLoading();
        } catch {
          setSendingCode(false);
          setSendFlag(false);
        }
      } else {
        try {
          const res = await sendBindCode({ target, verificationType: type, captchaVerification: code });
          const { errorCode } = res || {};
          if (errorCode === 407 || errorCode === 406) {
            onShowMsg?.();
            setSendingCode(false);
            setSendFlag(false);
            return;
          }
          setCountDown(DEFAULT_COUNTDOWN_TIME);
          cancelSendLoading();
        } catch {
          setSendingCode(false);
          setSendFlag(false);
        }
      }
    },
    [sendFlag, mode, codeText, apiUrl, DEFAULT_CODE_TEXT, verifyTarget, target, type, way, onShowMsg],
  );

  const handleSend = () => {
    const notVerified = verifyTarget();
    onVerifyClick?.(notVerified);
    if (notVerified || sendingCode) {
      return;
    }

    if (target && codeText === DEFAULT_CODE_TEXT) {
      setSendingCode(true);
      setIsSlideShow(true);
    }
  };

  useEffect(() => {
    if (type === 'MOBILE_PHONE') {
      const nextTarget = target ? target?.split(' ')?.[1] : '';
      setPhone(nextTarget);
    }
  }, [target]);

  useEffect(() => {
    let timer = -1;
    if (countDown > 0) {
      timer = window.setTimeout(() => {
        const time = countDown - 1;
        setCodeText(`${time} s`);
        setCountDown(time);
      }, 1e3);
    } else {
      setCodeText(DEFAULT_CODE_TEXT);
      setSendFlag(false);
    }
    return () => {
      clearTimeout(timer);
    };
  }, [countDown, DEFAULT_CODE_TEXT]);

  const codeNode = (
    <div
      role="link"
      className={codeClassName}
      style={{ color: (type === 'EMAIL' ? target : phone) ? '#FF5353' : '#8D8FA6' }}
      onClick={handleSend}
    >
      {sendingCode ? (
        <Image width={35} height={35} src={require('@public/imgs/login/loading.svg')} alt="loading" />
      ) : (
        codeText
      )}
    </div>
  );

  const closeVerify = () => {
    setSendingCode(false);
    setIsSlideShow(false);
  };

  const verifySuccess = async ({ captchaVerification }: CaptchaSuccess) => {
    setIsSlideShow(false);
    sendCode(captchaVerification);
  };

  return (
    <>
      <VerifySlideFixed isSlideShow={isSlideShow} onClose={closeVerify} success={verifySuccess} />
      <Input
        onChange={(e) => onChange(e.currentTarget.value)}
        onPressEnter={onPressEnter}
        placeholder={placeholder}
        maxLength={maxLength}
        suffixCls={cls(styles.code, codeClassName)}
        suffix={codeNode}
        className={className}
        value={value}
      />
    </>
  );
};

export default CodeInput;
